import dayjs, { Dayjs } from "utils/dayjs"

import Asset, { AssetOwnershipType, IPostAssetDto } from "models/Asset"
import { Currency, Purpose } from "config/constants"
import AmountWithCurrency from "models/AmountWithCurrency"
import { toDateTimeWithoutTimezone, stringToNumber } from "utils/utils"
import { prepareTranslations } from "utils/translations"
import { ICopyAssetRow, ICopyAssetState, effectiveValue, needsOwnerInput } from "./copyAssetRows"

/**
 * Create payload of one copy, derived from the source asset's own DTO with the
 * copy rules applied on top:
 *
 * - the values of the copy dialog (per copy or inherited) replace their source
 *   counterparts, and the initial reference numbers repeat the entered ones;
 * - the initial condition chosen in the ownership step applies to every copy,
 *   while the asset origin is always copied from the source;
 * - data taken over by assigning an existing LMID replaces the source's
 *   drawing number and technical data, including empty values;
 * - every copy starts operational with condition 100, its operating hours
 *   recorded now, and no expected return date;
 * - a monthly rental cost is sent only for borrowed copies and a reference
 *   value entered in the dialog only for purchased ones (a borrowed copy keeps
 *   the source's reference value); ownership-specific blocks of the other
 *   ownership are dropped, and a borrowed copy loses the sale purpose;
 * - the lending company of a borrowed copy is taken over from a borrowed
 *   source, and entered in the dialog when the source is purchased;
 * - the request statistic and disposal are never copied. Labels, connected
 *   assets, requests, maintenance, attachments, invoices, evaluation data and
 *   the histories are not part of the create payload, so they never reach the
 *   copy.
 */
export default function buildCopyAssetPayload({
    source,
    state,
    row,
    lmid,
    defaultCurrency,
    now = dayjs(),
}: {
    source: Asset
    state: ICopyAssetState
    row: ICopyAssetRow
    /** LMID to create with; undefined when the LMID is assigned later via the internal order number. */
    lmid: string | undefined
    defaultCurrency: Currency
    now?: Dayjs
}): IPostAssetDto {
    const dto = source.toDto({ noLmid: lmid === undefined })

    const isBorrowed = state.ownershipType === AssetOwnershipType.borrowed
    const constructionYear = stringToNumber(effectiveValue(state, row, "constructionYear"))
    const referenceNumberSale = effectiveValue(state, row, "referenceNumberSale").trim()
    const referenceNumberRental = effectiveValue(state, row, "referenceNumberRental").trim()
    const availabilityDate = effectiveValue(state, row, "availabilityDate")
    const monthlyRentalCostAmount = stringToNumber(effectiveValue(state, row, "monthlyRentalCostValue"))
    const referenceValueAmount = stringToNumber(effectiveValue(state, row, "referenceValue"))

    dto.ownershipType = state.ownershipType
    dto.lmid = lmid
    dto.status = effectiveValue(state, row, "status")
    dto.isSecondhand = state.isSecondhand

    dto.serialNumber = row.serialNumber || undefined
    dto.serialNumberMachine = row.serialNumberMachine || undefined
    dto.inventoryNo = row.inventoryNo || undefined
    dto.internalOrderNumber = effectiveValue(state, row, "internalOrderNumber") || undefined
    dto.supplierOrderNo = effectiveValue(state, row, "supplierOrderNo") || undefined
    dto.internalNotes = effectiveValue(state, row, "internalNotes") || undefined
    dto.constructionYear = constructionYear ?? dto.constructionYear
    dto.availabilityDate = availabilityDate?.isValid() ? toDateTimeWithoutTimezone(availabilityDate) : undefined

    dto.expectedReturnDate = undefined
    dto.isOperational = true
    dto.condition = 100
    dto.operationHoursTime = typeof dto.operationHours === "number" ? now.toISOString() : undefined

    dto.disposal = undefined
    dto.assetRequestStatistic = undefined

    dto.owner = !isBorrowed
        ? undefined
        : needsOwnerInput(state)
            ? { name: effectiveValue(state, row, "ownerName").trim() }
            : dto.owner
    dto.buyBack = isBorrowed ? null : dto.buyBack
    dto.purposes = isBorrowed ? dto.purposes?.filter(purpose => purpose !== Purpose.sale) : dto.purposes

    const overwrite = row.lmidOverwrite
    if (overwrite && "drawingNo" in overwrite) {
        dto.drawingNo = overwrite.drawingNo ?? undefined
    }
    if (overwrite?.assetData) {
        const assetData = overwrite.assetData
        dto.features = assetData.features ? prepareTranslations(assetData.features) : null
        dto.freightUnits = assetData.freightUnits
        dto.width = assetData.width ?? undefined
        dto.height = assetData.height ?? undefined
        dto.length = assetData.length ?? undefined
        dto.weight = assetData.weight ?? undefined
        dto.volume = assetData.volume ?? undefined
        dto.emissionLevel = assetData.emissionLevel ?? undefined
        dto.colorId = assetData.colorId ?? null
        dto.measurementSystemId = assetData.measurementSystemId ?? null
        dto.quickCouplingSystem = assetData.quickCouplingSystem ?? undefined
        dto.connectionType = assetData.connectionType ?? undefined
        dto.cuttingEdge = assetData.cuttingEdge ?? undefined
        dto.teeth = assetData.teeth ?? undefined
        dto.heavyDuty = assetData.heavyDuty ?? undefined
        dto.locking = assetData.locking ?? undefined
        dto.controlSystem = assetData.controlSystem ?? undefined
        dto.agility = assetData.agility ?? undefined
    }

    dto.assetFinancial = {
        ...dto.assetFinancial,
        referenceNumberSale,
        referenceNumberRental: referenceNumberRental || undefined,
        initialReferenceNumberSale: referenceNumberSale,
        initialReferenceNumberRental: referenceNumberRental || undefined,
        monthlyRentalCost: isBorrowed && monthlyRentalCostAmount !== null
            ? {
                validFrom: null,
                rentalCost: new AmountWithCurrency({
                    amount: monthlyRentalCostAmount,
                    currency: source.assetFinancial?.monthlyRentalCost?.rentalCost.currency || defaultCurrency,
                }),
            }
            : undefined,
        referenceValue: isBorrowed
            ? dto.assetFinancial?.referenceValue
            : referenceValueAmount !== null
                ? new AmountWithCurrency({
                    amount: referenceValueAmount,
                    currency: source.assetFinancial?.referenceValue?.currency || defaultCurrency,
                })
                : undefined,
    }

    return dto
}
